import React, { useState, useEffect, useRef } from 'react';
import { getNoGoTenders } from '../services/api';
import { formatDate } from '../utils/formatDate';
import './TenderList.css';

function NoGoTenderList({ user, canManage = false, onGoToDiscovery }) {
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  
  // Modal states
  const [viewingTender, setViewingTender] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showStagger, setShowStagger] = useState(false);
  const staggerShownRef = useRef(false);

  // Source options (static)
  const sourceOptions = [
    { value: 'gem', label: 'GeM (Government e-Marketplace)' },
    { value: 'cppp', label: 'CPPP (Central Public Procurement Portal)' },
    { value: 'state', label: 'State Portals' },
    { value: 'private', label: 'Private' },
  ];

  // Fetch tenders (optional overrides for clear filters)
  const fetchTenders = async (overrides = {}) => {
    try {
      setLoading(true);
      const response = await getNoGoTenders({
        source: overrides.source !== undefined ? overrides.source : sourceFilter,
        search: overrides.search !== undefined ? overrides.search : searchTerm,
      });
      if (response.success) {
        setTenders(response.tenders);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFilter]);

  useEffect(() => {
    if (!loading && tenders.length > 0 && !staggerShownRef.current) {
      staggerShownRef.current = true;
      setShowStagger(true);
    }
  }, [loading, tenders.length]);

  useEffect(() => {
    if (!showStagger) return;
    const t = setTimeout(() => setShowStagger(false), 800);
    return () => clearTimeout(t);
  }, [showStagger]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchTenders();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSourceFilter('');
  };

  const hasActiveFilters = searchTerm || sourceFilter;

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Get source badge class
  const getSourceClass = (source) => {
    const sourceClasses = {
      gem: 'source-gem',
      cppp: 'source-cppp',
      state: 'source-state',
      private: 'source-private',
    };
    return sourceClasses[source] || 'source-default';
  };

  return (
    <div className="tender-list-container">
      <div className="tender-list-header">
        <div className="header-title">
          <h2>No Go Tenders</h2>
          <p>Tenders that were marked as not to participate</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <form onSubmit={handleSearch} className="search-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="text"
            placeholder="Search by reference, title, or buyer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>
        
        <div className="filter-dropdowns">
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <option value="">All Sources</option>
            {sourceOptions.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          {hasActiveFilters && (
            <button type="button" className="btn-secondary" onClick={clearFilters} style={{ marginLeft: 8 }}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Loading State - skeleton table */}
      {loading ? (
        <div className="tenders-table-wrapper">
          <table className="tenders-table table-skeleton">
            <thead>
              <tr>
                <th style={{ width: '32px' }}></th>
                <th>Reference</th>
                <th>Title</th>
                <th>Source</th>
                <th>Buyer / Department</th>
                <th>Deadline</th>
                <th>Est. Value</th>
                <th>Products</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5].map((i) => (
                <tr key={i}>
                  <td><span className="skeleton-box" style={{ width: 20 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 100 }} /></td>
                  <td><span className="skeleton-box" style={{ width: '50%' }} /></td>
                  <td><span className="skeleton-box" style={{ width: 72 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 100 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 88 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 70 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 52 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 60 }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : tenders.length === 0 ? (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <h3>No tenders in No Go list</h3>
          <p>Tenders marked as No Go will appear here</p>
          {onGoToDiscovery && (
            <button type="button" onClick={onGoToDiscovery}>Go to Tender Discovery</button>
          )}
        </div>
      ) : (
        /* Tenders Table */
        <div className="tenders-table-wrapper">
          <table className="tenders-table">
            <thead>
              <tr>
                <th style={{ width: '32px' }}></th>
                <th>Reference</th>
                <th>Title</th>
                <th>Source</th>
                <th>Buyer / Department</th>
                <th>Deadline</th>
                <th>Est. Value</th>
                <th>Products</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody className={showStagger ? 'stagger-rows' : ''}>
              {tenders.map((tender, index) => (
                <React.Fragment key={tender.id}>
                  <tr
                    style={showStagger ? { animationDelay: `${index * 0.03}s` } : undefined}
                    onClick={() => setExpandedId(expandedId === tender.id ? null : tender.id)}
                    className={`tender-row tender-row-clickable deadline-passed ${expandedId === tender.id ? 'expanded' : ''}`}
                  >
                    <td className="expand-cell">
                      <svg className={`expand-icon ${expandedId === tender.id ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="ref-code ref-code-link"
                        onClick={(e) => { e.stopPropagation(); setViewingTender(tender); }}
                        title="View tender details"
                      >
                        {tender.tender_ref}
                      </button>
                    </td>
                    <td><span className="tender-title-inline">{tender.title || '–'}</span></td>
                    <td>
                      <span className={`source-badge ${getSourceClass(tender.source)}`}>
                        {tender.source_display}
                      </span>
                    </td>
                    <td>{tender.buyer_name || '-'}</td>
                    <td>{formatDate(tender.submission_deadline)}</td>
                    <td>{formatCurrency(tender.tender_value)}</td>
                    <td>
                      <span className="product-count">{(tender.products && tender.products.length) || 0} items</span>
                    </td>
                    <td>
                      <span className="status-badge status-no_go">No Go</span>
                    </td>
                  </tr>
                  {expandedId === tender.id && (
                    <tr className="tender-detail-row">
                      <td colSpan={9}>
                        <div className="tender-expanded-details">
                          <div className="tender-details-grid">
                            <div className="detail-item"><label>Buyer / Department</label><span>{tender.buyer_name || '-'}</span></div>
                            <div className="detail-item"><label>Submission Deadline</label><span>{formatDate(tender.submission_deadline)}</span></div>
                            <div className="detail-item"><label>Estimated Value</label><span>{formatCurrency(tender.tender_value)}</span></div>
                            <div className="detail-item"><label>Products</label><span>{(tender.products && tender.products.length) || 0} items</span></div>
                          </div>
                          {tender.description && <p className="tender-description-snippet">{tender.description}</p>}
                          <button type="button" className="link-btn" onClick={(e) => { e.stopPropagation(); setViewingTender(tender); }}>View full details →</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Total Count */}
      {!loading && tenders.length > 0 && (
        <div className="list-footer">
          <span>Total: {tenders.length} tenders</span>
        </div>
      )}

      {/* Tender View Modal */}
      {viewingTender && (
        <div className="modal-overlay" onClick={() => setViewingTender(null)}>
          <div className="tender-view-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setViewingTender(null)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            
            <div className="tender-view-content">
              <div className="tender-view-header">
                <span className={`source-badge ${getSourceClass(viewingTender.source)}`}>
                  {viewingTender.source_display}
                </span>
                <span className="status-badge status-no_go">
                  No Go
                </span>
              </div>
              
              <h2>{viewingTender.tender_ref}</h2>
              {viewingTender.title && <p className="tender-title-view">{viewingTender.title}</p>}
              
              <div className="tender-details-grid">
                <div className="detail-item">
                  <label>Buyer / Department</label>
                  <span>{viewingTender.buyer_name || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Submission Deadline</label>
                  <span>{formatDate(viewingTender.submission_deadline)}</span>
                </div>
                <div className="detail-item">
                  <label>Estimated Value</label>
                  <span className="value-highlight">{formatCurrency(viewingTender.tender_value)}</span>
                </div>
                <div className="detail-item">
                  <label>Products</label>
                  <span>{(viewingTender.products && viewingTender.products.length) || 0} items</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NoGoTenderList;
